import backgroundImageUrl from '@assets/background.svg?url';
import { Separator } from '@chakra-ui/react';
import {
    MyAccountMenu as BaseMyAccountMenu,
    BuildInfo,
    gravatarUrl,
} from '@ionq/legacy-ui';
import { buildInfo } from '@lib/buildInfo';
import { useShowBuildInfo } from '@lib/featureFlags';
import type React from 'react';
import { useMemo } from 'react';
import { FaFileContract } from 'react-icons/fa';
import { MdOutlinePrivacyTip, MdOutlineSettings } from 'react-icons/md';
import { RiContractLine } from 'react-icons/ri';
import { useLocation } from 'react-router';
import { getSignOutURL, signOut } from '../lib/session';
import { useSession } from './SessionProvider';

// Hardcoded on purpose; enterprise reads this from VITE_IONQ_WEBSITE_BASE_URL.
// Align if the policy links ever need to vary per environment.
const IONQ_WEBSITE_BASE_URL = 'https://ionq.com';

// Adapter over the shared, app-agnostic menu: identity comes from the real
// session (console-backend users.me) and the items are console business —
// the remote's account-settings route plus website policy links. Unlike the
// enterprise adapter there's no onboarding-tour item.
export function MyAccountMenu() {
    const { user } = useSession();
    const location = useLocation();
    const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    const showBuildInfo = useShowBuildInfo();
    // Same recipe as enterprise: 404 default so accounts without a
    // Gravatar fall back to the initials chip.
    const avatarURL = gravatarUrl(user.email, {
        size: 192,
        defaultImage: '404',
    });

    // Full in-app URL (search/hash included) so the post-login return lands
    // on the exact page state, not just the path.
    const signOutURL = useMemo(
        () =>
            getSignOutURL({
                email: user.email,
                targetUri: `${location.pathname}${location.search}${location.hash}`,
            }),
        [user.email, location.pathname, location.search, location.hash],
    );

    // Same-tab replace so the authed page doesn't survive in history behind
    // the sign-out; the href stays for middle-click/copy-link semantics.
    const onLogoutClick = (e: React.MouseEvent) => {
        // Modified clicks (cmd/ctrl/shift/middle) keep native link
        // behavior on the fallback href.
        if (
            e.metaKey ||
            e.ctrlKey ||
            e.shiftKey ||
            e.altKey ||
            e.button !== 0
        ) {
            return;
        }
        e.preventDefault();
        // Direct sign-out; the href stays for middle-click/copy-link.
        void signOut({
            email: user.email,
            targetUri: `${location.pathname}${location.search}${location.hash}`,
        });
    };

    return (
        <BaseMyAccountMenu
            name={fullName}
            // The bar shows the first name alone.
            triggerLabel={user.firstName || fullName || user.email}
            email={user.email}
            avatarUrl={avatarURL}
            logoutHref={signOutURL}
            onLogout={onLogoutClick}
            headerBackgroundUrl={backgroundImageUrl}
            buildInfo={showBuildInfo ? <BuildInfo {...buildInfo} /> : undefined}
        >
            {(dismiss) => (
                <>
                    <BaseMyAccountMenu.ItemLink
                        to='/settings/account'
                        label='Account Settings'
                        icon={MdOutlineSettings}
                        onClick={dismiss}
                    />
                    <Separator my={2} />
                    <BaseMyAccountMenu.ItemExternalLink
                        href={`${IONQ_WEBSITE_BASE_URL}/eula`}
                        label='End User License Agreement'
                        icon={FaFileContract}
                        onClick={dismiss}
                    />
                    <BaseMyAccountMenu.ItemExternalLink
                        href={`${IONQ_WEBSITE_BASE_URL}/terms`}
                        label='Terms of Service'
                        icon={RiContractLine}
                        onClick={dismiss}
                    />
                    <BaseMyAccountMenu.ItemExternalLink
                        href={`${IONQ_WEBSITE_BASE_URL}/privacy`}
                        label='Privacy Policy'
                        icon={MdOutlinePrivacyTip}
                        onClick={dismiss}
                    />
                </>
            )}
        </BaseMyAccountMenu>
    );
}
